import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useTableStoreByRoute } from "@/stores/useTableStore";
import CircularProgress from "@mui/material/CircularProgress";

import { CalendarQuickIcon, colors } from "@dotnet/telematics-design-system";

import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../../context/alert/types";
import { ROUTES, VEHICLES_ACTIONS } from "../../../../../../shared";
import { refreshTableAndUpdateDetails } from "../../../../../../shared/functions/functions";
import useResponse from "../../../../../../shared/hooks/useResponse";
import Button from "../../../../../Button/Button";
import { useDialog } from "../../../../../PageHeader/actions/hooks/useDialog";
import { Actions } from "../../../../../PageHeader/actions/types";
import Tooltip from "../../../../../Tooltip/Tooltip";
import useTableItemDetails from "../../../../hooks/useTableItemDetails";
import { DriverDetailsDto, VehicleDetailsDto } from "../../../../types";
import { QUICK_DOWNLOAD_STATUS } from "./types";
import VehicleQuickDownloadScheduleDialog from "./VehicleQuickDownloadScheduleDialog/VehicleQuickDownloadScheduleDialog";

interface ScheduleQuickDownloadProps {
  route: ROUTES;
  resourceAction: Actions;
  detailsData: VehicleDetailsDto | DriverDetailsDto;
}

const ScheduleQuickDownload = ({
  route,
  resourceAction,
  detailsData
}: ScheduleQuickDownloadProps) => {
  const { t } = useTranslation();

  const { postData } = useAxiosPrivate();
  const { handleCommonError, handleResponse } = useResponse();

  const { isDialogOpen, openDialog, closeDialog } = useDialog();
  const { getTableItemDetails } = useTableItemDetails();

  const {
    params: { contains }
  } = useTableStoreByRoute(route);

  const [isLoading, setIsLoading] = useState(false);

  const { id, disabled } = detailsData;

  const isVehicleQuickDownload =
    resourceAction === VEHICLES_ACTIONS.QuickScheduleDownload;

  const handleLinkClick = async () => {
    if (isVehicleQuickDownload) {
      openDialog(VEHICLES_ACTIONS.QuickScheduleDownload);
    } else {
      try {
        setIsLoading(true);
        await postData(ApiResources.DriverSchedulesQuickDownload, {
          driverId: id
        });

        handleContentRefresh();

        handleResponse(
          ALERT_STATUS.Info,
          t("Alert##quick schedule created##title"),
          t("Alert##quick schedule created##description##drivers")
        );
      } catch (error) {
        handleCommonError(error, t);
      }
      setIsLoading(false);
    }
  };

  const handleContentRefresh = () => {
    refreshTableAndUpdateDetails({
      valueInSearchField: contains,
      resourceId: id,
      itemHasOpenedDetails: true,
      getTableItemDetails,
      handleResponse
    });
  };

  const handleCloseDialog = () => {
    closeDialog(VEHICLES_ACTIONS.QuickScheduleDownload);
  };

  const getQuickDownloadStatus = () => {
    return disabled
      ? QUICK_DOWNLOAD_STATUS.Deactivated
      : QUICK_DOWNLOAD_STATUS.New;
  };

  const getButtonIcon = () =>
    isLoading ? (
      <CircularProgress
        size={12}
        sx={{
          color: colors.textDisabled
        }}
      />
    ) : (
      <CalendarQuickIcon
        style={{
          width: "14px",
          height: "14px"
        }}
      />
    );

  return (
    <>
      <Tooltip
        placement="top-end"
        size="medium"
        title={t(
          `Schedules##quick schedule##${resourceAction} ${getQuickDownloadStatus()}`
        )}
      >
        <div>
          <Button
            data-testid="schedule-quick-schedule-button"
            color="secondary"
            onClick={handleLinkClick}
            size="tiny"
            variant="iconLeft"
            disabled={disabled || isLoading}
            icon={getButtonIcon()}
          >
            {t("Schedules##quick schedule##title")}
          </Button>
        </div>
      </Tooltip>

      <VehicleQuickDownloadScheduleDialog
        vehicleId={id}
        isOpen={isDialogOpen[VEHICLES_ACTIONS.QuickScheduleDownload]}
        handleClose={handleCloseDialog}
      />
    </>
  );
};

export default ScheduleQuickDownload;
